import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Deal, ProductSubscription } from '../../shared/models';
import React from 'react';
import { useParams } from 'react-router-dom';
import { ApiRootUrl } from '../../shared/utils/api';

interface SubscriptionActionButtonProps {
	subscription: ProductSubscription;
	deal: Deal | null;
	onEdit: (subscription: ProductSubscription) => void;
	onEditSuccessTiers: (subscription: ProductSubscription) => void;
	onEditPaymentTerms: (subscription: ProductSubscription) => void;
	onChangeProduct: (subscription: ProductSubscription) => void;
	onDelete: (subscription: ProductSubscription) => void;
	onUpload: (subscription: ProductSubscription) => void;
	onCreateInvoice: (subscription: ProductSubscription) => void;
}

type PageActionItem = keyof typeof PageActionItemMap;
const PageActionItemMap = {
	Edit: 'Edit',
	SuccessTiers: 'Edit Tiers',
	PaymentTerms: 'Edit Payment Terms',
	ViewAgreement: 'View Agreement',
	ChangeProduct: 'Change Product',
	UploadAgreement: 'Upload Agreement',
	Delete: 'Delete',
	CreateInvoice: 'Create Success Fee Invoice',
};

export const SubscriptionActionButton: React.FC<SubscriptionActionButtonProps> = ({
	subscription,
	deal,
	onEdit,
	onEditSuccessTiers,
	onEditPaymentTerms,
	onChangeProduct,
	onUpload,
	onDelete,
	onCreateInvoice,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const { guid } = useParams<'guid'>();
	const contractUploaded = subscription.agreementFileName && subscription.agreementFileName.length > 0;
	const contractSignedOrLater = deal?.contractSignedOrLater ?? false;
	const generatedInvoice = deal?.hasGeneratedSuccessFeeInvoice ?? false;
	const canCreateSuccessInvoice =
		((subscription.successFeeType == 'Flat' && subscription.basisPoints && subscription.basisPoints > 0) ||
			(subscription.successFeeType == 'Tiered' &&
				subscription.successFeeTiers &&
				subscription.successFeeTiers.length > 0)) &&
		!generatedInvoice &&
		deal?.dealStatus == 'ActiveClosing';
	const paymentTermsAvailable = subscription.platformAccessPaymentSchedule == 'Custom';

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: PageActionItem) => {
		switch (action) {
			case 'Edit':
				onEdit(subscription);
				break;
			case 'SuccessTiers':
				onEditSuccessTiers(subscription);
				break;
			case 'ChangeProduct':
				onChangeProduct(subscription);
				break;
			case 'ViewAgreement':
				window.open(`${ApiRootUrl}/Api/Deals/${guid}/Subscriptions/${subscription.guid}/Download`, '_blank');
				break;
			case 'UploadAgreement':
				onUpload(subscription);
				break;
			case 'Delete':
				onDelete(subscription);
				break;
			case 'CreateInvoice':
				onCreateInvoice(subscription);
				break;
			case 'PaymentTerms':
				onEditPaymentTerms(subscription);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, actionItem: PageActionItem) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id="split-button-menu" autoFocusItem>
									{!contractSignedOrLater && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Edit')}>
											{PageActionItemMap['Edit']}
										</MenuItem>
									)}
									{!contractSignedOrLater && subscription.successFeeType == 'Tiered' && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'SuccessTiers')}>
											{PageActionItemMap['SuccessTiers']}
										</MenuItem>
									)}
									{paymentTermsAvailable && !contractSignedOrLater && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'PaymentTerms')}>
											{PageActionItemMap['PaymentTerms']}
										</MenuItem>
									)}
									{canCreateSuccessInvoice && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'CreateInvoice')}>
											{PageActionItemMap['CreateInvoice']}
										</MenuItem>
									)}
									{contractUploaded && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ViewAgreement')}>
											{PageActionItemMap['ViewAgreement']}
										</MenuItem>
									)}
									{!contractUploaded && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'UploadAgreement')}>
											{PageActionItemMap['UploadAgreement']}
										</MenuItem>
									)}
									{subscription.canDelete && !contractUploaded && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Delete')}>
											{PageActionItemMap['Delete']}
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
